import { Grid, Skeleton } from '@mui/material';
import { Field, Formik } from 'formik';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useMutation, useQueryClient } from 'react-query';
import {
	CerebroDrawer,
	CerebroForm,
	CerebroHeading,
	CerebroInput,
	CerebroLabel,
	CerebroMultiDropdown,
	CerebroMultiDropdownMenuItem,
	CerebroPrimaryButton,
	CerebroText,
	CerebroTextarea,
} from '../../../shared/components';
import { useApi } from '../../../shared/context';
import { QueryKeys, useLenderDeclineReasonsQuery } from '../../../shared/hooks';
import { DataRoomUser, ActiveDataRoomUser } from '../../../shared/models';
import { FormikUtils } from '../../../shared/utils';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { getApiResponseData } from '../../../shared/utils/axios';
import * as Yup from 'yup';

interface OptOutFormProps {
	dataRoomGuid: string | undefined;
	user: DataRoomUser | null | undefined;
	close: () => void;
}

export const OptOutForm: React.FC<OptOutFormProps> = ({ dataRoomGuid, user, close }) => {
	const api = useApi();
	const client = useQueryClient();
	const snackbar = useSnackbar();
	const declineReasonsQuery = useLenderDeclineReasonsQuery();
	const [showOther, setShowOther] = React.useState<boolean>(false);

	const onUpdateDeclineView = (selections: number[]): void => {
		setShowOther(selections.filter(num => num == -1).length > 0);
	};

	let OptOutFormSchema = Yup.object().shape({
		reEngagePoint: Yup.string().required('Re-Engage Point is required.'),
		reasonDetail: Yup.string().required('Details of Reason is required.'),
		reasons: Yup.array().min(1, 'At least one reason must be selected.').required('Select Decline Reason(s).'),
	});

	if (showOther) {
		OptOutFormSchema = OptOutFormSchema.shape({
			otherReason: Yup.string().required('Other reason is required'),
		});
	}

	const responseMutation = useMutation(
		async ({ values }: OptOutFormMutationVariables) => {
			const accessPatch = {
				reasons: values.reasons,
				otherReason: values.otherReason,
				reasonDetail: values.reasonDetail,
				reEngagePoint: values.reEngagePoint,
			};

			const { data, error } = await getApiResponseData<ActiveDataRoomUser>(
				api.post(CerebroApiEndpoints.DataRoomUsers.OptOut(dataRoomGuid, user?.guid), accessPatch)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				Promise.all([
					client.invalidateQueries(QueryKeys.DataRoomUsers.Root(dataRoomGuid)),
					client.invalidateQueries(QueryKeys.Milestones.Root(dataRoomGuid)),
					client.invalidateQueries(QueryKeys.DataRoomUsers.TeamMembersByRoom(dataRoomGuid)),
				]).finally(() => {
					snackbar.enqueueSnackbar('Lender was opted out.', { variant: 'success' });
					close();
				});
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={user != null} onClose={() => close()} PaperProps={{ sx: { width: '500px' } }}>
			<CerebroHeading size="h1" gutterBottom>
				{user?.firstName} {user?.lastName} ({user?.bankName})
			</CerebroHeading>
			<Formik
				validationSchema={OptOutFormSchema}
				initialValues={{
					reasons: [] as number[],
					otherReason: '',
					reasonDetail: '',
					reEngagePoint: '',
				}}
				onSubmit={FormikUtils.MutateOnSubmit(responseMutation.mutate)}
			>
				{({ isSubmitting, errors, touched, handleSubmit, values }) => {
					return (
						<CerebroForm onSubmit={handleSubmit}>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								<Grid item xs={8}>
									<CerebroLabel id="reasons" required>
										Opt-Out Reasons
									</CerebroLabel>
									{declineReasonsQuery.isLoading && <Skeleton height={60} />}
									{declineReasonsQuery.isSuccess && (
										<>
											<Field
												id="reasons"
												as={CerebroMultiDropdown}
												name="reasons"
												onClose={() => onUpdateDeclineView(values.reasons)}
												disabled={isSubmitting}
											>
												{declineReasonsQuery.data.map(reason => (
													<CerebroMultiDropdownMenuItem key={reason.guid} value={reason.id}>
														{reason.name}
													</CerebroMultiDropdownMenuItem>
												))}
											</Field>
											{FormikUtils.IsInvalid('reasons', errors, touched) && (
												<CerebroText variant="error">{errors.reasons}</CerebroText>
											)}
										</>
									)}
								</Grid>
								{showOther && (
									<Grid item xs={8}>
										<CerebroLabel id="otherReason" required>
											Other Reason
										</CerebroLabel>
										<Field
											id="otherReason"
											name="otherReason"
											as={CerebroInput}
											error={FormikUtils.IsInvalid('otherReason', errors, touched)}
										/>
										{FormikUtils.IsInvalid('otherReason', errors, touched) && (
											<CerebroText variant="error">{errors.otherReason}</CerebroText>
										)}
									</Grid>
								)}
								<Grid item xs={8}>
									<CerebroLabel id="reasonDetail" required>
										Details of Reason
									</CerebroLabel>
									<Field
										id="reasonDetail"
										as={CerebroTextarea}
										name="reasonDetail"
										disabled={isSubmitting}
										error={FormikUtils.IsInvalid('reasonDetail', errors, touched)}
									/>
									{FormikUtils.IsInvalid('reasonDetail', errors, touched) && (
										<CerebroText variant="error">{errors.reasonDetail}</CerebroText>
									)}
								</Grid>

								<Grid item xs={8}>
									<CerebroLabel id="reEngagePoint" required>
										Re-Engage Point
									</CerebroLabel>
									<Field
										id="reEngagePoint"
										as={CerebroTextarea}
										name="reEngagePoint"
										disabled={isSubmitting}
										error={FormikUtils.IsInvalid('reEngagePoint', errors, touched)}
									/>
									{FormikUtils.IsInvalid('reEngagePoint', errors, touched) && (
										<CerebroText variant="error">{errors.reasonDetail}</CerebroText>
									)}
								</Grid>

								<Grid item xs={8}>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Save
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

type OptOutFormMutationVariables = FormikUtils.MutateOnSubmitVariables<OptOutFormValues>;
interface OptOutFormValues {
	reasons: number[];
	otherReason: string;
	reasonDetail: string;
	reEngagePoint: string;
}
